import { mutationOptions, queryOptions } from "@tanstack/react-query";
import {
  CHANGE_EMAIL_VERSION_2,
  CREDENTIAL_RESET_REQUEST,
  GET_LOVENSE_LIVE_RECORDING,
  LOGIN_FOR_WEB,
  PASSWORD_RESET_REQUEST,
  REG_FOR_WEB,
  RESET_PWD_FOR_WEB,
  VERIFY_PASSWORD_RESET_AUTH_CODE,
} from "~/constants/apiEndpoints";
import { APP_LANGUAGE, WEB_APPLICATION_NAME } from "~/constants/app";
import { apiClient } from "~/lib/apiClient";
import { getCurrentApplicationId } from "~/lib/applicationId";
import { sha1 } from "~/lib/crypto";
import { queryKeys } from "~/lib/queryKeys";
import { useAuthStore } from "~/stores/useAuthStore";
import { API_ERROR_CODES, ApiError } from "~/types/api";
import { toGender } from "~/types/user";
import type {
  CredentialResetRequestRequest,
  CredentialResetRequestResponse,
  CredentialResetRequestResult,
  GetLovenseLiveRecordingResponse,
  LoginRequest,
  LoginResponse,
  PasswordResetAuthCodeResult,
  PasswordResetRequestRequest,
  RegisterRequest,
  RegisterResponse,
  ResetPasswordRequest,
  ResetPasswordResult,
} from "./types";

export const loginMutationOptions = mutationOptions({
  mutationFn: (data: LoginRequest) =>
    apiClient<LoginResponse>(LOGIN_FOR_WEB, {
      ...(data as unknown as Record<string, unknown>),
      web_application: WEB_APPLICATION_NAME,
    }),
});

export const registerMutationOptions = mutationOptions({
  mutationFn: (data: RegisterRequest) =>
    apiClient<RegisterResponse>(REG_FOR_WEB, {
      ...(data as unknown as Record<string, unknown>),
      web_application: WEB_APPLICATION_NAME,
    }),
});

/**
 * パスワード再設定リンクのメール送信を BE に依頼する。
 *
 * `application_id` は必須で送る。BE は未指定時に Utage(15) へフォールバックする
 * 後方互換実装のため、送らないと Sina のユーザーが検索対象から外れる。
 */
export const passwordResetRequestMutationOptions = mutationOptions({
  mutationFn: (data: PasswordResetRequestRequest) =>
    apiClient<unknown>(PASSWORD_RESET_REQUEST, {
      ...data,
      application_id: getCurrentApplicationId(),
    }),
});

/**
 * ログイン情報再設定の確認コードを発行する。
 *
 * BE は端末識別子を application_id 完全一致で逆引きし、application_id を文字列で読む。
 */
export const credentialResetRequestMutationOptions = mutationOptions({
  mutationFn: async ({
    email,
    idfv,
    androidOriginalId,
  }: CredentialResetRequestRequest): Promise<CredentialResetRequestResult> => {
    const data = await apiClient<CredentialResetRequestResponse>(
      CREDENTIAL_RESET_REQUEST,
      {
        email,
        application_id: getCurrentApplicationId(),
        ...(idfv ? { idfv } : {}),
        ...(androidOriginalId
          ? { android_original_id: androidOriginalId }
          : {}),
      },
    );
    return { authCode: data.auth_code };
  },
});

/**
 * 再設定リンクに含まれる認証コードの有効性を検証する。
 *
 * 無効なコードは INVALID_AUTH_CODE で返るが、これは「リンク切れ」という
 * 正常な分岐なのでエラーとして throw せず isVerified: false に変換する。
 * 通信エラー等はそのまま throw してルートのエラー表示に委ねる。
 * 有効期限が30分のため、キャッシュを再利用せず毎回検証する。
 */
export const passwordResetAuthCodeQueryOptions = (authCode: string) =>
  queryOptions({
    queryKey: queryKeys.auth.passwordResetAuthCode(authCode),
    queryFn: async (): Promise<PasswordResetAuthCodeResult> => {
      try {
        const data = await apiClient<{ email?: string | null } | undefined>(
          VERIFY_PASSWORD_RESET_AUTH_CODE,
          { auth_code: authCode },
        );
        // BE 未デプロイ時は data ごと存在しないため、任意項目として扱う
        return { isVerified: true, email: data?.email ?? undefined };
      } catch (error) {
        if (
          error instanceof ApiError &&
          error.code === API_ERROR_CODES.INVALID_AUTH_CODE
        ) {
          return { isVerified: false };
        }
        throw error;
      }
    },
    staleTime: 0,
    retry: false,
  });

/**
 * Sakura ログイン前画面の背景動画差し替え用。
 *
 * レスポンスは排他的（`recording_id` → `buzz_live_recording_id` → 空オブジェクトの優先順）。
 * 未ログイン状態から呼ぶため application_id のみを送る。失敗・該当なしの場合は
 * 呼び出し側で静的な背景動画にフォールバックする想定なので、リトライはしない。
 */
export const lovenseLiveRecordingQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.auth.lovenseLiveRecording(),
    queryFn: () =>
      apiClient<GetLovenseLiveRecordingResponse>(GET_LOVENSE_LIVE_RECORDING, {
        application_id: getCurrentApplicationId(),
      }),
    staleTime: 0,
    retry: false,
  });

/**
 * パスワードを新しい値へ更新し、必要ならログイン用メールアドレスも更新する。
 *
 * `new_pwd` は SHA1、`original_pwd` は平文を送る。BE は `new_pwd` をさらに MD5 して
 * `Users.PASSWORD` に保存し、ログイン時も MD5(受信 pwd) と比較するため、
 * ログイン（useLogin）が SHA1 を送っているのと同じ形式に揃える必要がある。
 * 平文側は `Users.ORIGINAL_PASSOWRD`（平文保管フィールド）に格納される。
 *
 * メールアドレスを変更する場合のみ3段になる:
 * 1. `reset_pwd_for_web` でパスワードを更新（token 不要）
 * 2. 新パスワードで `login_for_web` を呼び token を取得（`change_email_version_2` は token 必須）
 * 3. `change_email_version_2` で email とパスワードを同時更新
 *
 * 1 が成功した後に 2 / 3 が失敗しても巻き戻さない。パスワードは既に新しい値になっており、
 * ユーザーは手動ログインできるため、どこまで到達したかを返り値で呼び出し元に伝える。
 */
export const resetPasswordMutationOptions = mutationOptions({
  mutationFn: async ({
    userId,
    newPassword,
    currentEmail,
    newEmail,
  }: ResetPasswordRequest): Promise<ResetPasswordResult> => {
    const hashedPwd = await sha1(newPassword);

    await apiClient<unknown>(RESET_PWD_FOR_WEB, {
      from_user_id: userId,
      new_pwd: hashedPwd,
      original_pwd: newPassword,
    });

    // 同一アドレスは BE が EMAIL_REGISTED を返すため、変更がない場合は後続を実行しない
    if (!currentEmail || !newEmail || newEmail === currentEmail) {
      return { status: "passwordOnly" };
    }

    let isLoggedIn = false;
    try {
      const loginRes = await apiClient<LoginResponse>(LOGIN_FOR_WEB, {
        email: currentEmail,
        pwd: hashedPwd,
        application_id: getCurrentApplicationId(),
        login_time: getLoginTime(),
        machine_type: "web",
        language: APP_LANGUAGE,
        web_application: WEB_APPLICATION_NAME,
      });

      // apiClient は useAuthStore の token を注入するため、メール更新前に反映する
      useAuthStore.getState().setAuth(loginRes.token, loginRes.user_id, {
        id: loginRes.user_id,
        name: loginRes.user_name,
        email: loginRes.email,
        gender: toGender(loginRes.gender),
        age: loginRes.age,
        avatarUrl: loginRes.ava_id || null,
        region: loginRes.region,
      });
      isLoggedIn = true;

      await apiClient<unknown>(CHANGE_EMAIL_VERSION_2, {
        email: newEmail,
        new_pwd: hashedPwd,
        original_pwd: newPassword,
      });
    } catch (error) {
      return {
        status: "emailFailed",
        isLoggedIn,
        emailErrorCode: error instanceof ApiError ? error.code : undefined,
      };
    }

    return { status: "emailUpdated" };
  },
});

/**
 * Native 新規登録フロー。
 *
 * 1. `reg_for_web` を email/pwd 省略で呼ぶ → BE 自動生成された email / pwd(SHA1) / userId を取得
 * 2. その email/pwd で `login_for_web` を呼んで token を取得
 *
 * Utage-Web の `/api/native/register` ルートと同じ二段呼び出し。
 */
export const nativeRegisterMutationOptions = mutationOptions({
  mutationFn: async (data: {
    register: RegisterRequest;
    loginExtras: Pick<
      LoginRequest,
      "idfv" | "gps_adid" | "ad_id" | "android_original_id" | "app_version"
    >;
  }) => {
    const registerRes = await apiClient<RegisterResponse>(REG_FOR_WEB, {
      ...(data.register as unknown as Record<string, unknown>),
      web_application: WEB_APPLICATION_NAME,
    });

    const loginReq: LoginRequest = {
      email: registerRes.email,
      pwd: registerRes.pwd,
      application_id: data.register.application_id,
      ...(data.register.idfv || data.register.device_id
        ? { device_id: data.register.idfv ?? data.register.device_id }
        : {}),
      login_time: getLoginTime(),
      machine_type: "mobile",
      language: data.register.language,
      ...data.loginExtras,
    };

    const loginRes = await apiClient<LoginResponse>(LOGIN_FOR_WEB, {
      ...(loginReq as unknown as Record<string, unknown>),
      web_application: WEB_APPLICATION_NAME,
    });

    return {
      email: registerRes.email,
      pwd: registerRes.pwd,
      userId: registerRes.user_id,
      userName: loginRes.user_name || registerRes.user_name,
      token: loginRes.token,
      // reg_for_web で直接付与されたかを判定する（login_for_web の got_registered_point より確実）
      gotRegisteredPoint: registerRes.is_register_bonus_granted ?? false,
      user: loginRes,
    };
  },
});

const getLoginTime = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    String(d.getFullYear()) +
    pad(d.getMonth() + 1) +
    pad(d.getDate()) +
    pad(d.getHours()) +
    pad(d.getMinutes()) +
    pad(d.getSeconds())
  );
};
